/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2022-01-20 15:08:51
 *
 *******************************************************/

import _ from 'lodash';
import React, { Component } from 'react';
import { Icon, TextStyle, Button } from '@shopify/polaris';
import {
  ArrowUpMinor,
  ArrowDownMinor,
  HideMinor,
  EditMinor,
  DeleteMinor
} from '@shopify/polaris-icons';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { FilterIconDisable } from 'src/components/containers/common/icon/filterIcon';
import { RenderProductTooltip } from './strategy-detail';
import { CONDUCTOR_FOR_ATTRIBUTES, PRODUCT_ATTRIBUTES } from 'src/constants/merchandising-v2';

type Props = {
  consequences: Object,
  ruleId: string,
  handleEditPin: Function,
  handleEditAttribute: Function,
  handleDelete: Function,
  index: number
};

type ComposedProps = Props & WithI18nProps;

class ProductDetail extends Component<ComposedProps> {
  renderAttributesDisplay = attributes => {
    const { optionsList, i18n, moneyFormat } = this.props;

    const result = [];
    _.map(attributes, item => {
      const { attribute, conductor, value } = item;
      // Attribute display
      // only display for attribute not empty

      if (
        !_.isEmpty(attribute) &&
        !_.isEmpty(conductor) &&
        // In case value is date => isEmpty = true, so need more condition
        (!_.isEmpty(value) ||
          (typeof value === 'object' && ['created_at', 'published_at'].includes(attribute)))
      ) {
        const attributeObject = _.find(
          PRODUCT_ATTRIBUTES,
          productAttribute => productAttribute.value === attribute
        );
        const attributeLabel = _.isEmpty(attributeObject)
          ? _.upperFirst(attribute)
          : i18n.translate(attributeObject.label);

        // Conductor display
        const conductorList =
          CONDUCTOR_FOR_ATTRIBUTES[_.includes(attribute, 'opt_') ? 'option' : attribute];

        const conductorObject = _.isEmpty(conductorList)
          ? {}
          : _.find(conductorList, conductorItem => conductorItem.value === conductor);
        const conductorLabel = _.isEmpty(conductorObject)
          ? i18n.translate('AdminHTML.MerchandisingV2.Constant.conductorForProduct_Label')
          : i18n.translate(conductorObject.label);

        // Value display
        let valueLabel = '';
        switch (attribute) {
          case 'product_id': {
            valueLabel = (
              <RenderProductTooltip item={value} i18n={i18n} moneyFormat={moneyFormat} />
            );
            break;
          }
          // option
          default: {
            const { label = '' } = value;
            valueLabel = <TextStyle variation="strong">{label}</TextStyle>;
          }
        }
        let attributeLabelOption;
        if (_.startsWith(attribute, 'opt_')) {
          const option = _.find(optionsList, { name: attribute.replace('opt_', '') });
          attributeLabelOption = _.get(option, 'originalName', attributeLabel);
        }
        const attributeDisplay = (
          <div style={{ marginTop: 5 }}>
            {i18n.translate('AdminHTML.MerchandisingV2.ListRules.consequence_Attribute', {
              nt_attribute: attributeLabelOption ? attributeLabelOption : attributeLabel,
              nt_conductor: conductorLabel,
              nt_value: valueLabel
            })}
          </div>
        );
        result.push(attributeDisplay);
      }
    });
    return result;
  };

  render() {
    const { i18n, type, products, handleEditAttribute, handleDelete, viewOnly } = this.props;

    const attributeHeading = {
      boost: {
        title: i18n.translate('AdminHTML.MerchandisingV2.Rule.card3_Boost_Products'),
        icon: <Icon source={ArrowUpMinor} color="subdued" />
      },
      demote: {
        title: i18n.translate('AdminHTML.MerchandisingV2.Rule.card3_Demote_Products'),
        icon: <Icon source={ArrowDownMinor} color="subdued" />
      },
      hide: {
        title: i18n.translate('AdminHTML.MerchandisingV2.Rule.card3_Hide_Products'),
        icon: <Icon source={HideMinor} color="subdued" />
      },
      filter: {
        title: i18n.translate('AdminHTML.MerchandisingV2.Rule.card3_Filter_Products'),
        icon: <Icon source={FilterIconDisable} color="subdued" />
      }
    };
    const attributesDisplay = this.renderAttributesDisplay(
      products.map(value => ({ attribute: 'product_id', conductor: 'is', value }))
    );
    return (
      <div className="consequence-detail">
        {!viewOnly && (
          <div className="consequence-detail-action">
            <Button plain icon={EditMinor} onClick={() => handleEditAttribute(type)}></Button>
            <Button plain icon={DeleteMinor} onClick={() => handleDelete(type)}></Button>
          </div>
        )}
        <div className="consequence-detail-title">
          {attributeHeading[type].icon}
          <TextStyle variation="subdued">
            <span style={{ textTransform: 'uppercase' }}>{attributeHeading[type].title}</span>
          </TextStyle>
        </div>
        <div className="consequence-detail-content">{attributesDisplay}</div>
      </div>
    );
  }
}

export default withI18n()(ProductDetail);
